import type { InterfaceLanguageReply, SaveInterfaceLanguageInput } from "../backend/models/InterfaceLanguageModel";
import {
  isSelectableInterfacePreference, type InitialInterfaceLanguage, type InterfaceLanguageAuthority,
} from "./interfaceLanguagePreference";
import { resolveInterfaceLocale, type LanguagePreference, type LocaleCode } from "./localeRegistry";

export interface InterfaceLanguageState extends InitialInterfaceLanguage {
  accountId: string | null;
  authority: InterfaceLanguageAuthority | null;
  status: "idle" | "loading" | "saving";
  error: "load" | "save" | "conflict" | "catalog" | null;
}
interface Dependencies {
  languages: () => string[];
  guest: () => InitialInterfaceLanguage;
  loadCatalog: (locale: LocaleCode) => Promise<unknown>;
  changeLanguage: (locale: LocaleCode) => Promise<unknown>;
  request: (accountId: string, input?: SaveInterfaceLanguageInput, signal?: AbortSignal) => Promise<InterfaceLanguageReply>;
  rememberGuest: (preference: LanguagePreference) => void;
  rememberAccount: (accountId: string, language: InterfaceLanguageAuthority) => void;
  clearAccount: () => void;
  source?: () => SaveInterfaceLanguageInput["source"];
  onChange?: (state: InterfaceLanguageState, reason: "account" | "choice" | "system") => void;
}

/** One owner and one preference revision at a time; stale async work never wins. */
export function createInterfaceLanguageController(initial: InitialInterfaceLanguage, dependencies: Dependencies) {
  let state: InterfaceLanguageState = { ...initial, accountId: null, authority: null, status: "idle", error: null };
  const listeners = new Set<() => void>();
  let epoch = 0;
  let initializedOwner = false;
  let pending: AbortController | null = null;
  let rendering = Promise.resolve();
  // Storage can be unavailable. Keep guest intent independently of account authority for this session.
  let sessionGuestPreference = initial.source === "guest" ? initial.preference : null;
  let queuedReconciliation: { accountId: string; fallback: InterfaceLanguageAuthority | null; force: boolean } | null = null;
  let queuedSystemRefresh = false;
  const publish = (update: Partial<InterfaceLanguageState>) => {
    state = { ...state, ...update };
    listeners.forEach(listener => listener());
  };
  const begin = () => {
    pending?.abort();
    pending = new AbortController();
    return { id: ++epoch, signal: pending.signal };
  };
  const isCurrent = (id: number) => id === epoch;
  const reportChange = (reason: "account" | "choice" | "system") => {
    try { dependencies.onChange?.(state, reason); } catch { /* Diagnostics cannot undo a saved preference. */ }
  };
  const render = async (locale: LocaleCode, id: number) => {
    await dependencies.loadCatalog(locale);
    if (!isCurrent(id)) return false;
    // Tolgee changes are serialized too: a slow previous change cannot settle
    // after a new account or choice and restore the previous language.
    const next = rendering.catch(() => undefined).then(async () => {
      if (isCurrent(id)) await dependencies.changeLanguage(locale);
    });
    rendering = next.then(() => undefined);
    await next;
    return isCurrent(id);
  };
  const applyAuthority = async (language: InterfaceLanguageAuthority, id: number, reason: "account" | "choice" | "system", conflict = false) => {
    if (!isCurrent(id) || !state.accountId) return;
    dependencies.rememberAccount(state.accountId, language);
    publish({ authority: language, preference: language.preference, source: "account" });
    const effectiveLocale = resolveInterfaceLocale(language.preference, dependencies.languages());
    try {
      if (await render(effectiveLocale, id)) {
        publish({ effectiveLocale, status: "idle", error: conflict ? "conflict" : null });
        reportChange(reason);
      }
    } catch {
      if (isCurrent(id)) publish({ status: "idle", error: "catalog" });
    }
  };

  const reconcileAccount = async (accountId: string | null, fallback: InterfaceLanguageAuthority | null = null, force = false) => {
    if (!force && initializedOwner && accountId === state.accountId && (!accountId || fallback?.revision === state.authority?.revision)) return;
    if (accountId && accountId === state.accountId && state.status !== "idle") {
      // Firestore can deliver our own revision before PATCH returns. Preserve the
      // operation and coalesce a recheck instead of cancelling a successful save.
      queuedReconciliation = { accountId, fallback, force };
      return;
    }
    queuedReconciliation = null;
    initializedOwner = true;
    const { id, signal } = begin();
    publish({ accountId, authority: fallback, status: "loading", error: null });
    if (!accountId) {
      dependencies.clearAccount();
      const guest: InitialInterfaceLanguage = sessionGuestPreference === null ? dependencies.guest() : {
        preference: sessionGuestPreference, effectiveLocale: resolveInterfaceLocale(sessionGuestPreference, dependencies.languages()), source: "guest",
      };
      // Retain the guest target even if its catalog fails. Retry must never
      // promote the previous account's still-rendered language to guest intent.
      publish({ preference: guest.preference, source: guest.source });
      try {
        if (await render(guest.effectiveLocale, id)) publish({ ...guest, status: "idle" });
      } catch { if (isCurrent(id)) publish({ status: "idle", error: "catalog" }); }
      finally { await drainReconciliation(id); }
      return;
    }
    try {
      let reply = await dependencies.request(accountId, undefined, signal);
      if (!isCurrent(id)) return;
      const effectiveLocale = resolveInterfaceLocale(reply.language.preference, dependencies.languages());
      // No manual preference is inferred from a guest hint or a legacy field.
      if (reply.language.effectiveLocale !== effectiveLocale) {
        try {
          reply = await dependencies.request(accountId, { effectiveLocale, expectedRevision: reply.language.revision,
            source: dependencies.source?.() ?? "web" }, signal);
        } catch {
          await applyAuthority(reply.language, id, "account");
          if (isCurrent(id)) publish({ error: state.error ?? "save" });
          return;
        }
      }
      await applyAuthority(reply.language, id, "account", reply.conflict);
    } catch {
      if (!isCurrent(id)) return;
      if (fallback) await applyAuthority(fallback, id, "account");
      if (isCurrent(id)) publish({ status: "idle", error: state.error ?? "load" });
    } finally {
      await drainReconciliation(id);
    }
  };

  async function drainReconciliation(id: number) {
    if (!isCurrent(id) || state.status !== "idle") return;
    if (queuedReconciliation) {
      const queued = queuedReconciliation;
      queuedReconciliation = null;
      await reconcileAccount(queued.accountId, queued.fallback, queued.force);
      // An own-save snapshot may already match and return without starting
      // another operation. It must not consume the pending device refresh.
      if (isCurrent(id)) await drainReconciliation(id);
    } else if (queuedSystemRefresh) {
      queuedSystemRefresh = false;
      await refreshSystemLanguage();
    }
  }

  const choose = async (preference: LanguagePreference): Promise<boolean> => {
    if (!isSelectableInterfacePreference(preference) || state.status !== "idle") return false;
    const { id, signal } = begin();
    const accountId = state.accountId;
    const authority = state.authority;
    const effectiveLocale = resolveInterfaceLocale(preference, dependencies.languages());
    publish({ status: "saving", error: null });
    try {
      // Fetch before saving so a missing catalog leaves the current choice usable.
      try { await dependencies.loadCatalog(effectiveLocale); }
      catch {
        if (isCurrent(id)) publish({ status: "idle", error: "catalog" });
        return false;
      }
      if (!isCurrent(id)) return false;
      if (accountId) {
        const reply = await dependencies.request(accountId, { preference, effectiveLocale,
          expectedRevision: authority?.revision ?? null, source: dependencies.source?.() ?? "web" }, signal);
        await applyAuthority(reply.language, id, "choice", reply.conflict);
        return isCurrent(id) && !reply.conflict && !state.error;
      }
      if (!await render(effectiveLocale, id)) return false;
      sessionGuestPreference = preference;
      dependencies.rememberGuest(preference);
      publish({ preference, effectiveLocale, source: "guest", status: "idle", error: null });
      reportChange("choice");
      return true;
    } catch {
      if (isCurrent(id)) publish({ status: "idle", error: "save" });
      return false;
    } finally {
      await drainReconciliation(id);
    }
  };

  async function refreshSystemLanguage() {
    // Foreground/OS updates can arrive during catalog loading or a preference
    // save. Coalesce them and resolve the latest list once that work settles.
    if (state.status !== "idle") { queuedSystemRefresh = true; return; }
    queuedSystemRefresh = false;
    if (state.preference !== "system") return;
    const effectiveLocale = resolveInterfaceLocale("system", dependencies.languages());
    if (effectiveLocale === state.effectiveLocale && state.error !== "catalog" &&
      (!state.accountId || state.authority?.effectiveLocale === effectiveLocale)) return;
    if (state.accountId) await reconcileAccount(state.accountId, state.authority, true);
    else {
      const { id } = begin();
      publish({ status: "loading" });
      try {
        if (await render(effectiveLocale, id)) {
          publish({ effectiveLocale, status: "idle", error: null });
          reportChange("system");
        }
      } catch { if (isCurrent(id)) publish({ status: "idle", error: "catalog" }); }
      finally { await drainReconciliation(id); }
    }
  }

  return {
    getSnapshot: () => state,
    subscribe: (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; },
    reconcileAccount,
    choose,
    refreshSystemLanguage,
    retry: () => state.accountId ? reconcileAccount(state.accountId, state.authority, true) : reconcileAccount(null, null, true),
  };
}
